import React, { useState } from "react";
import { motion } from "framer-motion";
import { LogIn, Sparkles, Ticket } from "lucide-react";
import Avatar from "../Avatar";
import PasswordInput from "./PasswordInput";
import { loginAccount, registerWithCode } from "@/lib/api";

// FastAPI 422 returns an array of {msg,...}; map any detail shape to a string.
function formatApiErrorDetail(detail) {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Full-screen sign-in gate — the first thing every visitor sees.
// Nobody reaches the app until they sign in or create an account.
// Registering from this screen still adopts any guest-cookie data the
// browser may hold from before forced sign-in was enabled.
//
// `onForgotPassword` is wired into the parent App router so we don't
// have to know about routing internals here.
export default function SignInScreen({ onAuthed, onForgotPassword }) {
    const [mode, setMode] = useState("login"); // "login" | "register"
    const [email, setEmail] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [password, setPassword] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const isRegister = mode === "register";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            const cleanEmail = email.trim().toLowerCase();
            const data = isRegister
                ? await registerWithCode(cleanEmail, password, displayName.trim(), inviteCode.trim().toUpperCase() || null)
                : await loginAccount(cleanEmail, password);
            onAuthed(data);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "something went sideways — try again?");
            setBusy(false);
        }
    };

    const switchMode = (m) => { setMode(m); setError(""); };

    const inputCls = "w-full bg-cream rounded-2xl px-4 py-3 font-body text-sm text-warm-900 outline-none border-2 border-transparent focus:border-terracotta transition-all";

    return (
        <div
            className="App font-body min-h-screen [min-height:100dvh] flex items-center justify-center px-5 py-10"
            data-testid="auth-gate"
        >
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="w-full max-w-sm"
            >
                <div className="flex flex-col items-center mb-5">
                    <Avatar mood="happy" size={140} />
                    <h1 className="font-display text-4xl text-warm-900 mt-2">Mochi</h1>
                    <p className="font-body text-sm text-warm-500 mt-1">your cozy productivity buddy 🍡</p>
                </div>

                <div className="bg-surface rounded-clay shadow-clay p-5">
                    {/* Mode tabs */}
                    <div className="flex gap-1.5 bg-cream p-1 rounded-full mb-4">
                        {[
                            { id: "login", label: "Sign in" },
                            { id: "register", label: "Create account" },
                        ].map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => switchMode(t.id)}
                                className={`flex-1 py-2 rounded-full font-display text-sm transition-all ${
                                    mode === t.id ? "bg-terracotta text-cream shadow-clay" : "text-warm-500 hover:text-warm-800"
                                }`}
                                data-testid={`auth-tab-${t.id}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <form onSubmit={submit} className="space-y-2.5" data-testid="auth-form">
                        <input
                            type="email"
                            required
                            maxLength={254}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={isRegister ? "your email" : "email"}
                            autoCapitalize="none"
                            autoCorrect="off"
                            autoComplete={isRegister ? "email" : "username"}
                            inputMode="email"
                            className={inputCls}
                            data-testid="auth-email-input"
                        />
                        {isRegister && (
                            <input
                                type="text"
                                maxLength={40}
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="what should mochi call you? (optional)"
                                autoCorrect="off"
                                autoComplete="nickname"
                                className={inputCls}
                                data-testid="auth-display-name-input"
                            />
                        )}
                        <PasswordInput
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={isRegister ? "password (6+ chars)" : "password"}
                            minLength={isRegister ? 6 : 1}
                            autoComplete={isRegister ? "new-password" : "current-password"}
                            className={inputCls}
                            testId="auth-password-input"
                        />
                        {isRegister && (
                            <p className="font-body text-[10px] text-warm-500 leading-relaxed px-1">
                                your email is how you sign in. we'll send a quick welcome note, then only ever password resets — never marketing 🌸
                            </p>
                        )}
                        {isRegister && (
                            <div className="rounded-2xl bg-cream/70 border border-terracotta/25 p-2.5 mt-1" data-testid="auth-beta-card">
                                <label htmlFor="auth-invite-code" className="flex items-center gap-1.5 font-display text-xs text-warm-800 mb-1.5 px-0.5">
                                    <Ticket size={13} className="text-terracotta" /> Beta invite code
                                    <span className="font-body text-[10px] text-warm-500 normal-case">— optional</span>
                                </label>
                                <input
                                    id="auth-invite-code"
                                    type="text"
                                    maxLength={40}
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                    placeholder="MOCHI-XXXXXX"
                                    autoCapitalize="characters"
                                    autoCorrect="off"
                                    autoComplete="off"
                                    className="w-full bg-cream rounded-xl px-3 py-2.5 font-body text-sm text-warm-900 tracking-wider outline-none border-2 border-transparent focus:border-terracotta transition-all"
                                    data-testid="auth-invite-code-input"
                                />
                                <p className="font-body text-[10px] text-warm-500 mt-1.5 px-0.5 leading-relaxed">
                                    got a beta invite? drop it here 🎟️ if your email's on the list, you're in without one.
                                </p>
                            </div>
                        )}
                        {error && (
                            <p className="font-body text-xs text-terracotta px-1" data-testid="auth-error">{error}</p>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-cream font-display text-base py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
                            data-testid="auth-submit-btn"
                        >
                            {busy ? "one sec…" : isRegister ? (<><Sparkles size={16} /> Claim your Mochi</>) : (<><LogIn size={16} /> Sign in</>)}
                        </button>
                    </form>

                    <div className="mt-3 flex items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={() => switchMode(isRegister ? "login" : "register")}
                            className="font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="auth-mode-toggle"
                        >
                            {isRegister ? "already have an account? sign in" : "new here? create an account"}
                        </button>
                        {!isRegister && onForgotPassword && (
                            <button
                                type="button"
                                onClick={onForgotPassword}
                                className="font-body text-[11px] text-terracotta hover:text-terracotta-hover transition-colors"
                                data-testid="auth-forgot-password-link"
                            >
                                forgot password?
                            </button>
                        )}
                    </div>
                </div>

                <p className="font-body text-[11px] text-warm-500 text-center mt-4">
                    your tasks, habits and chats stay private to your account 🌸
                </p>
            </motion.div>
        </div>
    );
}
